import { CAMPAIGN_PLACEHOLDER_TOKENS } from '@constants/placeholders';
import { LOCALE } from '@constants/locale';
import {
    WA_BUTTON_TYPE,
    WA_COMPONENT_TYPE,
    WA_HEADER_FORMAT,
    WA_LIMITS,
    WA_MAPPING_SOURCE,
    WA_URL_SUFFIX,
    WA_URL_SUFFIX_PARAM,
    WA_LINK_TYPE,
} from '@constants/campaignTemplates';

// Mirrors server WhatsAppTemplateContentTools.PlaceholderRegex.
const PLACEHOLDER_PATTERN = '\\{\\{\\s*([a-zA-Z0-9_]+)\\s*\\}\\}';

export const matchPlaceholders = text => [...(text || '').matchAll(new RegExp(PLACEHOLDER_PATTERN, 'g'))];

export const waToDisplayTokens = text =>
    (text || '').replace(new RegExp(PLACEHOLDER_PATTERN, 'g'), (whole, param) =>
        CAMPAIGN_PLACEHOLDER_TOKENS.includes(param) ? `[${param}]` : whole,
    );

export const displayToWaTokens = text =>
    (text || '').replace(/\[([a-zA-Z0-9_]+)\]/g, (whole, token) =>
        CAMPAIGN_PLACEHOLDER_TOKENS.includes(token) ? `{{${token}}}` : whole,
    );

// {{first-name}}, {{}} or an unmatched }} read as plain text to the regex but Meta rejects them.
export const hasMalformedPlaceholders = text => {
    const stripped = (text || '').replace(new RegExp(PLACEHOLDER_PATTERN, 'g'), '');
    return stripped.includes('{{') || stripped.includes('}}');
};

// Sample values mirror server CampaignPlaceholderResolver.SampleValues().
export const WA_SAMPLE_VALUES = {
    name: 'Alex',
    last_name: 'Smith',
    location: 'Riverside Café',
    brand: 'Riverside Café',
    geomodifier: 'Downtown',
    primary_phone: '+1 555 010 2030',
    email: 'hello@example.com',
    website: 'https://example.com',
    address_line_1: '12 Main Street',
    address_line_2: 'Suite 4',
    city: 'Springfield',
    state: 'IL',
    postal_code: '62701',
    country: 'United States',
};

// Meta treats en/en_US/en_GB (and pt_BR/pt_PT, es/es_MX) as separate template languages with no
// behavioral difference — offering several variants only splits the same template into
// duplicate buckets, so one English, one Spanish and one Portuguese are picked per account locale.
export const waLanguagesForLocale = locale => [
    locale === LOCALE.UNITED_KINGDOM ? 'en_GB' : 'en',
    locale === LOCALE.MEXICO ? 'es_MX' : 'es',
    locale === LOCALE.PORTUGAL ? 'pt_PT' : 'pt_BR',
    'uk',
    'de',
    'fr',
    'it',
];

const WA_DEFAULT_LANGUAGE_BY_LOCALE = {
    [LOCALE.UNITED_KINGDOM]: 'en_GB',
    [LOCALE.UKRAINE]: 'uk',
    [LOCALE.SPAIN]: 'es',
    [LOCALE.PORTUGAL]: 'pt_PT',
    [LOCALE.BRAZIL]: 'pt_BR',
    [LOCALE.GERMANY]: 'de',
    [LOCALE.MEXICO]: 'es_MX',
};

export const defaultWaLanguage = locale => WA_DEFAULT_LANGUAGE_BY_LOCALE[locale] || 'en';

export const slugifyTemplateName = value =>
    value
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .slice(0, WA_LIMITS.NAME);

export const extractTextPlaceholders = (headerText, bodyText) => {
    const keys = [];
    [headerText, bodyText].forEach(text => {
        matchPlaceholders(text).forEach(match => {
            if (!keys.includes(match[1])) keys.push(match[1]);
        });
    });
    return keys;
};

export const createEmptyDraft = () => ({
    name: '',
    language: 'en',
    category: 'MARKETING',
    waba: '',
    headerFormat: WA_HEADER_FORMAT.NONE,
    headerText: '',
    headerMediaUrl: '',
    body: '',
    footer: '',
    buttons: [],
    mappings: [],
});

const stripUrlSuffix = url => (url?.endsWith(WA_URL_SUFFIX) ? url.slice(0, -WA_URL_SUFFIX.length) : url || '');

export const parseTemplate = template => {
    const components = template.components || [];
    const header = components.find(c => c.type === WA_COMPONENT_TYPE.HEADER);
    const body = components.find(c => c.type === WA_COMPONENT_TYPE.BODY);
    const footer = components.find(c => c.type === WA_COMPONENT_TYPE.FOOTER);
    const buttons = components.find(c => c.type === WA_COMPONENT_TYPE.BUTTONS)?.buttons || [];

    return {
        name: template.name || '',
        language: template.language || 'en',
        category: template.category || 'MARKETING',
        waba: template.waba || '',
        headerFormat: header ? header.format : WA_HEADER_FORMAT.NONE,
        headerText: header?.format === WA_HEADER_FORMAT.TEXT ? header.text || '' : '',
        headerMediaUrl: template.headerMediaUrl || '',
        body: body?.text || '',
        footer: footer?.text || '',
        buttons: buttons.map(button => ({
            type: button.type,
            text: button.text || '',
            url: button.type === WA_BUTTON_TYPE.URL ? stripUrlSuffix(button.url) : '',
            dynamic: button.type === WA_BUTTON_TYPE.URL && !!button.url?.endsWith(WA_URL_SUFFIX),
            linkType:
                button.type === WA_BUTTON_TYPE.URL && button.url?.endsWith(WA_URL_SUFFIX)
                    ? button.linkType || inferLinkType(button.url)
                    : '',
            phoneNumber: button.phoneNumber || '',
        })),
        mappings: (template.variableMappings || []).map(mapping => ({
            param: mapping.param,
            source: mapping.source,
            token: mapping.token || '',
        })),
    };
};

export const buildComponents = draft => {
    const components = [];
    if (draft.headerFormat === WA_HEADER_FORMAT.TEXT) {
        components.push({ type: WA_COMPONENT_TYPE.HEADER, format: WA_HEADER_FORMAT.TEXT, text: draft.headerText });
    } else if (draft.headerFormat === WA_HEADER_FORMAT.IMAGE) {
        components.push({ type: WA_COMPONENT_TYPE.HEADER, format: WA_HEADER_FORMAT.IMAGE });
    }
    components.push({ type: WA_COMPONENT_TYPE.BODY, text: draft.body });
    if (draft.footer.trim()) {
        components.push({ type: WA_COMPONENT_TYPE.FOOTER, text: draft.footer });
    }
    if (draft.buttons.length > 0) {
        components.push({
            type: WA_COMPONENT_TYPE.BUTTONS,
            buttons: draft.buttons.map(button => ({
                type: button.type,
                text: button.text,
                url: button.type === WA_BUTTON_TYPE.URL ? `${button.url}${button.dynamic ? WA_URL_SUFFIX : ''}` : null,
                linkType: button.type === WA_BUTTON_TYPE.URL && button.dynamic ? button.linkType || null : null,
                phoneNumber: button.type === WA_BUTTON_TYPE.PHONE_NUMBER ? button.phoneNumber : null,
            })),
        });
    }
    return components;
};

export const buildWriteModel = draft => ({
    name: draft.name,
    language: draft.language,
    category: draft.category,
    waba: draft.waba || null,
    components: buildComponents(draft),
    variableMappings: draft.mappings.map(mapping =>
        mapping.source === WA_MAPPING_SOURCE.FIELD
            ? { param: mapping.param, source: mapping.source, token: mapping.token }
            : { param: mapping.param, source: mapping.source },
    ),
    headerMediaUrl: draft.headerFormat === WA_HEADER_FORMAT.IMAGE ? draft.headerMediaUrl || null : null,
});

/**
 * True when the draft only changes mappings and/or the header image — the FR-26 setup path.
 * Both sides are canonicalized through parseTemplate/buildComponents so API shape differences
 * (null vs missing keys) don't register as content edits.
 */
export const isContentUnchanged = (template, draft) =>
    JSON.stringify(buildComponents(parseTemplate(template))) === JSON.stringify(buildComponents(draft));

/**
 * Validation for setup-only saves of externally created templates: their content is approved
 * by Meta and may legally violate our creation lint, so only the completion inputs are checked.
 */
export const lintSetupOnly = (draft, t) => {
    const errors = {};

    if (draft.headerFormat === WA_HEADER_FORMAT.IMAGE && !draft.headerMediaUrl) {
        errors.header = t('TemplateLibrary.waHeaderImageRequired');
    }

    if (draft.mappings.some(mapping => mapping.source === WA_MAPPING_SOURCE.FIELD && !mapping.token)) {
        errors.variables = t('TemplateLibrary.waMappingRequired');
    }

    return errors;
};

// The authored base path pins a dynamic button to one destination; infer for external
// templates that predate the explicit annotation.
export const inferLinkType = url =>
    url?.endsWith(`/s/${WA_URL_SUFFIX}`)
        ? WA_LINK_TYPE.SURVEY
        : url?.endsWith(`/o/${WA_URL_SUFFIX}`)
          ? WA_LINK_TYPE.COUPON
          : '';

export const hasDynamicUrlButton = buttons =>
    buttons.some(button => button.type === WA_BUTTON_TYPE.URL && button.dynamic);

export const isAbsoluteHttpUrl = value => {
    if (!/^https?:\/\//.test(value)) return false;
    try {
        new URL(value);
        return true;
    } catch {
        return false;
    }
};

// Re-derives the mapping rows after a content change: keeps rows for surviving variables,
// defaults new ones (known field token → that field, otherwise an unmapped field row the
// user must resolve) and auto-manages the URL-button link slot row.
export const syncMappings = draft => {
    const params = extractTextPlaceholders(
        draft.headerFormat === WA_HEADER_FORMAT.TEXT ? draft.headerText : '',
        draft.body,
    );
    const mappings = params.map(param => {
        const existing = draft.mappings.find(mapping => mapping.param === param);
        if (existing) return existing;
        return {
            param,
            source: WA_MAPPING_SOURCE.FIELD,
            token: CAMPAIGN_PLACEHOLDER_TOKENS.includes(param) ? param : '',
        };
    });
    if (hasDynamicUrlButton(draft.buttons)) {
        mappings.push({ param: WA_URL_SUFFIX_PARAM, source: WA_MAPPING_SOURCE.LINK_SLOT, token: '' });
    }
    return mappings;
};

export const substituteSamples = (text, mappings) =>
    (text || '').replace(new RegExp(PLACEHOLDER_PATTERN, 'g'), (whole, param) => {
        const mapping = (mappings || []).find(m => m.param === param);
        if (mapping?.source === WA_MAPPING_SOURCE.FIELD && WA_SAMPLE_VALUES[mapping.token]) {
            return WA_SAMPLE_VALUES[mapping.token];
        }
        return whole;
    });

// Client-side mirror of server WhatsAppTemplateLint — friendly, per-section messages.
// Short inputs deliberately have no visible counter, so length caps are validated here.
export const lintDraft = (draft, t) => {
    const errors = {};

    if (!draft.name || !/^[a-z0-9_]+$/.test(draft.name)) {
        errors.name = t('TemplateLibrary.waNameInvalid');
    }

    if (draft.headerFormat === WA_HEADER_FORMAT.TEXT) {
        if (!draft.headerText.trim()) {
            errors.header = t('validation.required');
        } else if (draft.headerText.length > WA_LIMITS.HEADER) {
            errors.header = t('validation.maxLength', { count: WA_LIMITS.HEADER });
        } else if (matchPlaceholders(draft.headerText).length > 1) {
            errors.header = t('TemplateLibrary.waHeaderOneVariable');
        }
    }

    if (draft.footer.length > WA_LIMITS.FOOTER) {
        errors.footer = t('validation.maxLength', { count: WA_LIMITS.FOOTER });
    }
    if (draft.headerFormat === WA_HEADER_FORMAT.IMAGE && !draft.headerMediaUrl) {
        errors.header = t('TemplateLibrary.waHeaderImageRequired');
    }

    const body = draft.body.trim();
    if (hasMalformedPlaceholders(draft.headerText) || hasMalformedPlaceholders(draft.footer)) {
        errors.header = hasMalformedPlaceholders(draft.headerText)
            ? t('TemplateLibrary.waMalformedVariable')
            : errors.header;
        errors.footer = hasMalformedPlaceholders(draft.footer)
            ? t('TemplateLibrary.waMalformedVariable')
            : errors.footer;
    }
    if (!body) {
        errors.body = t('validation.required');
    } else if (draft.body.length > WA_LIMITS.BODY) {
        errors.body = t('validation.maxLength', { count: WA_LIMITS.BODY });
    } else if (hasMalformedPlaceholders(body)) {
        errors.body = t('TemplateLibrary.waMalformedVariable');
    } else {
        const edgeVariable = matchPlaceholders(body).some(
            match => match.index === 0 || match.index + match[0].length === body.length,
        );
        if (edgeVariable) {
            errors.body = t('TemplateLibrary.waBodyEdgeVariable');
        } else {
            const keys = extractTextPlaceholders(
                draft.headerFormat === WA_HEADER_FORMAT.TEXT ? draft.headerText : '',
                draft.body,
            );
            const numbered = keys.filter(key => /^\d+$/.test(key));
            if (numbered.length > 0 && numbered.length !== keys.length) {
                errors.body = t('TemplateLibrary.waBodyMixedVariables');
            } else if (keys.some(key => !CAMPAIGN_PLACEHOLDER_TOKENS.includes(key))) {
                // Only field mappings can fill variables now, so authored content is limited
                // to the supported field vocabulary.
                errors.body = t('TemplateLibrary.waUnknownVariable');
            }
        }
    }

    const quickReplyFlags = draft.buttons.map(button => button.type === WA_BUTTON_TYPE.QUICK_REPLY);
    const quickReplyGroups = quickReplyFlags.filter(
        (isQuickReply, index) => isQuickReply && (index === 0 || !quickReplyFlags[index - 1]),
    ).length;
    if (quickReplyGroups > 1) {
        errors.buttons = t('TemplateLibrary.waQuickRepliesGrouped');
    } else if (
        draft.buttons.some(button => {
            if (!button.text.trim()) return true;
            if (button.type === WA_BUTTON_TYPE.URL) return !button.url.trim();
            if (button.type === WA_BUTTON_TYPE.PHONE_NUMBER) return !button.phoneNumber.trim();
            return false;
        })
    ) {
        errors.buttons = t('TemplateLibrary.waButtonsIncomplete');
    } else if (draft.buttons.some(button => button.text.length > WA_LIMITS.BUTTON_LABEL)) {
        errors.buttons = t('validation.maxLength', { count: WA_LIMITS.BUTTON_LABEL });
    } else if (
        draft.buttons.some(button => button.type === WA_BUTTON_TYPE.URL && !isAbsoluteHttpUrl(button.url.trim()))
    ) {
        // A missing REACT_APP_SURVEY_DOMAIN would otherwise produce relative bases that only
        // fail at Meta submission.
        errors.buttons = t('TemplateLibrary.waUrlAbsolute');
    }

    if (draft.mappings.some(mapping => mapping.source === WA_MAPPING_SOURCE.FIELD && !mapping.token)) {
        errors.variables = t('TemplateLibrary.waMappingRequired');
    }

    return errors;
};
